import React, { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { connectAmbulanceCall } from "../../../pages/actions/actions";
import { UseGetStoreData } from "../../../hooks/getStoreData/UseGetStoreData";
import { updateAmbulanceData } from "../../../http";
import { ShowAlert } from "../../../utils/showAlert";
import { setSelectedProviderId } from "../../../redux/ticketUpdateSlice/ticketUpdateSlice";
import style from "./ambulanceProviderCard.module.scss";

interface MobileContact {
  countryCode: string;
  number: string;
  isPrimary: boolean;
}

interface CoverageArea {
  _id: string;
  name: string;
  city: string;
  rangeInKm: number;
  distanceInMeters: number;
  ambulancePlaced: boolean;
}

export interface AmbulanceProviderData {
  _id: string;
  provider: {
    _id: string;
    operatorName: string;
    contact: {
      personName: string;
      mobile: MobileContact[];
    };
    ambulanceTypes: string[];
    availability: string;
    address: {
      addressLine1: string;
      city: string;
      state: string;
      pinCode: string;
    };
    score: number;
  };
  coverageAreas: CoverageArea[];
}

interface AmbulanceProviderCardProps {
  data: AmbulanceProviderData;
  isShortlisted: boolean;
  isSelected: boolean;
  onShortlist: (provider: AmbulanceProviderData) => void;
  shortlistedProviders: AmbulanceProviderData[];
}

// Pure helper — builds the wa.me deep-link URL 
const buildWhatsappUrl = (rawPhone: string, message: string): string => {
  const digits = rawPhone.replace(/\D/g, "");
  const encoded = encodeURIComponent(message);
  return `https://wa.me/${digits}?text=${encoded}`;
};

export const AmbulanceProviderCard: React.FC<AmbulanceProviderCardProps> = ({ data, isShortlisted, isSelected, onShortlist, shortlistedProviders }) => {
  const { provider, coverageAreas } = data;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { getTicketData, getAmbulanceMessage } = UseGetStoreData();
  const handleCall = useCallback((number: string) => {
    connectAmbulanceCall({ customerNumber: number }, getTicketData._id);
  }, [getTicketData._id]);

  const handleWhatsApp = useCallback(
    (e: React.MouseEvent, contact: MobileContact) => {
      e.stopPropagation();
      const rawPhone = `${contact.countryCode ?? ""}${contact.number}`;
      const url = buildWhatsappUrl(rawPhone, getAmbulanceMessage);
      window.open(url, "_blank", "noopener,noreferrer");
    },
    [getAmbulanceMessage]
  );

  const handleSelectProvider = async () => {
    if (isSelected) return;
    try {
      await updateAmbulanceData(getTicketData._id, {
        selectedProvider: {
          providerId: provider._id,
          providerName: provider.operatorName,
          primaryContact: provider.contact.mobile.find((m) => m.isPrimary),
        },
        shortlistedProviders: shortlistedProviders.map((p) => ({
          providerId: p.provider._id,
          providerName: p.provider.operatorName,
          primaryContact: p.provider.contact.mobile.find((m) => m.isPrimary),
        })),
      });
      dispatch(setSelectedProviderId(provider._id));
      ShowAlert("success", `${provider.operatorName} selected successfully`);
      navigate(-1);
    } catch {
      ShowAlert("error", "Failed to select provider. Please try again.");
    }
  };

  const allContacts = provider.contact.mobile;

  const placedAreas = coverageAreas.filter((a) => a.ambulancePlaced);
  const candidateAreas = placedAreas.length > 0 ? placedAreas : coverageAreas;

  const closestArea = candidateAreas.reduce<CoverageArea | null>(
    (prev, curr) => (prev === null || curr.distanceInMeters < prev.distanceInMeters ? curr : prev),
    null
  );
  const distanceKm = closestArea ? (closestArea.distanceInMeters / 1000).toFixed(2) : null;
  const distanceLabel = closestArea?.ambulancePlaced ? "Precise" : "Approx.";

  const addressParts = [
    provider.address?.addressLine1,
    provider.address?.city,
    provider.address?.state,
    provider.address?.pinCode,
  ].filter(Boolean);
  const addressLine = addressParts.join(", ");

  const coverageNames = coverageAreas.map((a) => a.name).join(", ");

  const rating = provider.score > 0 ? (provider.score / 20).toFixed(1) : "4.5";

  return (
    <div className={`${style.card} ${isShortlisted ? style.card_shortlisted : ""} ${isSelected ? style.card_selected : ""}`}>
      {/* Header */}
      <div className={style.header}>
        <div className={style.header_left}>
          <div className={style.icon_box}>🚑</div>
          <div>
            <div className={style.name}>{provider.operatorName}</div>
            <div className={style.rating}>⭐ {rating}/5.0</div>
          </div>
        </div>
        {distanceKm && (
          <div className={style.distance_box}>
            <div className={style.distance_km}>{distanceKm} km</div>
            <div className={closestArea?.ambulancePlaced ? style.distance_label_precise : style.distance_label_approx}>{distanceLabel}</div>
          </div>
        )}
      </div>

      {/* Body */}
      <div className={style.body}>
        {addressLine && (
          <div className={style.info_row}>
            <span className={style.info_icon}>📍</span>
            <span className={style.info_text}>{addressLine}</span>
          </div>
        )}

        {allContacts.map((m, i) => (
          <div key={i} className={style.info_row}>
            <div className={style.phone_pill} onClick={() => handleCall(m.number)}>
              <div className={style.phone_pill_icon}>
                <img className={style.phone_pill_img} src="/images/phone.png" alt="phone" />
              </div>
              <span className={style.phone_pill_text}>{m.countryCode} {m.number}</span>
            </div>
            {!m.isPrimary && <span className={style.alt_badge}>Alt</span>}
            {m.number && (
              <button
                className={style.whatsapp_btn}
                onClick={(e) => handleWhatsApp(e, m)}
                aria-label={`Send WhatsApp message to ${m.countryCode ?? ""}${m.number}`}
                title="Send WhatsApp message"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="#25D366" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20.52 3.449C18.24 1.245 15.24 0 12.045 0 5.463 0 .104 5.334.101 11.893c0 2.096.549 4.14 1.595 5.945L0 24l6.335-1.652c1.746.943 3.71 1.444 5.71 1.445h.006c6.585 0 11.946-5.336 11.949-11.896.001-3.176-1.24-6.165-3.48-8.448zm-8.475 18.307h-.005c-1.774 0-3.513-.474-5.03-1.37l-.361-.214-3.741.977.999-3.648-.235-.374a9.86 9.86 0 0 1-1.516-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.88 9.884zm5.42-7.405c-.297-.148-1.758-.867-2.03-.966-.272-.099-.47-.148-.669.148-.198.296-.767.965-.94 1.164-.173.199-.347.223-.644.074-.297-.149-1.255-.461-2.39-1.462-.883-.78-1.48-1.745-1.653-2.042-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.148-.174.198-.298.298-.497.099-.198.05-.372-.025-.521-.075-.148-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.372-.01-.571-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.463 1.065 2.876 1.213 3.074.148.198 2.096 3.2 5.077 4.487.709.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                </svg>
              </button>
            )}
          </div>
        ))}

        {coverageNames && (
          <div className={style.info_row}>
            <span className={style.info_icon}>🛡️</span>
            <span className={style.info_text}>Coverage: {coverageNames}</span>
          </div>
        )}
      </div>

      {/* Ambulance type tags */}
      {provider.ambulanceTypes.length > 0 && (
        <div className={style.tags}>
          {provider.ambulanceTypes.map((type) => (
            <span key={type} className={style.tag}>{type}</span>
          ))}
        </div>
      )}

      <div className={style.btn_row}>
        <button
          className={`${style.shortlist_btn} ${isShortlisted ? style.shortlist_btn_active : ""}`}
          onClick={() => onShortlist(data)}
        >
          {isShortlisted ? "Shortlisted ✓" : "Shortlist"}
        </button>
        <button
          className={`${style.select_btn} ${isSelected ? style.select_btn_active : ""}`}
          onClick={handleSelectProvider}
        >
          {isSelected ? "Selected ✓" : "Select Provider"}
        </button>
      </div>
    </div>
  );
};
